import React, { useEffect, useRef, useState } from 'react';
import useAdvancedParallax from '../hooks/useAdvancedParallax';
import useMousePosition from '../hooks/useMousePosition';
import useReducedMotion from '../hooks/useReducedMotion';
import './FloatingParticles.css';

/**
 * FloatingParticles component for creating interactive particle effects
 * @param {object} props - Component props
 * @param {number} props.particleCount - Number of particles to render
 * @param {string} props.particleType - Type of particles ('dots', 'shapes', 'mixed')
 * @param {boolean} props.enableMouse - Enable mouse interaction
 * @param {boolean} props.enableScroll - Enable scroll parallax
 * @param {string} props.colorScheme - Color scheme ('warm', 'cool', 'neutral')
 * @param {number} props.intensity - Animation intensity (0.1-1.0)
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FloatingParticles component
 */
const FloatingParticles = ({
  particleCount = 8,
  particleType = 'mixed',
  enableMouse = true,
  enableScroll = true,
  colorScheme = 'warm',
  intensity = 0.7,
  className = ''
}) => {
  const containerRef = useRef(null);
  const [particles, setParticles] = useState([]);
  const { isReducedMotion } = useReducedMotion();
  const { x: mouseX, y: mouseY, isMouseMoving } = useMousePosition(enableMouse);

  // Parallax configuration for the container
  const { ref: parallaxRef, isVisible, hasIntersected } = useAdvancedParallax({
    scrollSpeed: 1.0,
    mouseSpeed: 0.3,
    enableMouse: enableMouse,
    enableScroll: enableScroll,
    axis: 'both',
    mouseInfluence: 0.6
  });

  // Generate particle data
  useEffect(() => {
    if (isReducedMotion) return;

    const generateParticles = () => {
      const newParticles = [];
      const colors = {
        warm: ['#FFDAB9', '#FFE4C4', '#FFF8DC', '#F0E68C'],
        cool: ['#E6F3FF', '#CCE7FF', '#B3DAFF', '#99CCFF'],
        neutral: ['#F5F5F5', '#E8E8E8', '#DCDCDC', '#D0D0D0']
      };

      const shapes = {
        dots: 'circle',
        shapes: ['circle', 'square', 'triangle'],
        mixed: ['circle', 'square', 'triangle', 'diamond']
      };

      for (let i = 0; i < particleCount; i++) {
        const particle = {
          id: i,
          x: Math.random() * 100, // Percentage position
          y: Math.random() * 100,
          size: Math.random() * 8 + 4, // 4-12px
          color: colors[colorScheme][Math.floor(Math.random() * colors[colorScheme].length)],
          shape: Array.isArray(shapes[particleType]) 
            ? shapes[particleType][Math.floor(Math.random() * shapes[particleType].length)]
            : shapes[particleType],
          animationDelay: Math.random() * 5, // 0-5s delay
          animationDuration: Math.random() * 10 + 10, // 10-20s duration
          opacity: Math.random() * 0.6 + 0.2, // 0.2-0.8 opacity
          mouseInfluence: Math.random() * 0.5 + 0.3 // 0.3-0.8 influence
        };
        newParticles.push(particle);
      }
      setParticles(newParticles);
    };

    generateParticles();
  }, [particleCount, particleType, colorScheme, isReducedMotion]);

  // Calculate mouse influence on particles
  const getMouseInfluence = (particle) => {
    if (!enableMouse || !isMouseMoving || !hasIntersected) return { x: 0, y: 0 };

    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return { x: 0, y: 0 };

    const centerX = containerRect.left + containerRect.width / 2;
    const centerY = containerRect.top + containerRect.height / 2;
    
    const deltaX = (mouseX - centerX) / containerRect.width;
    const deltaY = (mouseY - centerY) / containerRect.height;
    
    const influence = particle.mouseInfluence * intensity;
    
    return {
      x: deltaX * influence * 20, // Max 20px movement
      y: deltaY * influence * 20
    };
  };

  if (isReducedMotion) {
    return null; // Don't render particles for reduced motion users
  }

  return (
    <div
      ref={(el) => {
        containerRef.current = el;
        if (parallaxRef) {
          parallaxRef.current = el;
        }
      }}
      className={`floating-particles ${className}`}
      data-is-visible={isVisible}
      data-has-intersected={hasIntersected}
    >
      {particles.map((particle) => {
        const mouseInfluence = getMouseInfluence(particle);
        
        return (
          <div
            key={particle.id}
            className={`particle particle-${particle.shape}`}
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              backgroundColor: particle.color,
              opacity: particle.opacity,
              animationDelay: `${particle.animationDelay}s`,
              animationDuration: `${particle.animationDuration}s`,
              transform: `translate(${mouseInfluence.x}px, ${mouseInfluence.y}px)`,
              transition: isMouseMoving ? 'transform 0.3s ease-out' : 'none'
            }}
          />
        );
      })}
    </div>
  );
};

export default FloatingParticles;
